<template>
	<FadeSlideGroup>
		<template v-for="group in normalizedGroups" :key="group.key">
			<v-slide-group-item>
				<v-chip v-if="!group.hasLevels" :color="isSimpleGroupSelected(group) ? 'primary' : undefined" :variant="isSimpleGroupSelected(group) ? 'tonal' : 'outlined'" selected-class="text-primary" @click="toggleSimpleGroup(group)" class="ma-1">
					<template #prepend v-if="isSimpleGroupSelected(group)">
						<span class="quick-row-group__order">{{ getGroupOrder(group) }}</span>
					</template>
					{{ group.label }}
				</v-chip>

				<v-menu v-else location="bottom" :model-value="isMenuOpen(group.key)" :close-on-content-click="false" @update:model-value="setMenuOpen(group.key, $event)">
					<template #activator="{ props: menuProps }">
						<v-chip v-bind="menuProps" :color="isLevelGroupSelected(group) ? 'primary' : undefined" :variant="isLevelGroupSelected(group) ? 'tonal' : 'outlined'" selected-class="text-primary" class="ma-1">
							<template #prepend v-if="isLevelGroupSelected(group)">
								<span class="quick-row-group__order">{{ getGroupOrder(group) }}</span>
							</template>
							{{ getGroupDisplayLabel(group) }}
						</v-chip>
					</template>

					<v-list density="compact" min-width="220">
						<v-list-item v-for="level in group.levels" :key="level.key" :title="level.label" :active="isLevelSelected(group, level)" @click="selectGroupLevel(group, level)">
							<template #append>
								<v-icon v-if="isLevelSelected(group, level)" icon="check" size="18" />
							</template>
						</v-list-item>
					</v-list>
				</v-menu>
			</v-slide-group-item>
		</template>
	</FadeSlideGroup>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import FadeSlideGroup from '@/components/common/FadeSlideGroup.vue';

const props = defineProps({
	groups: {
		type: Array,
		default: () => [],
	},
	modelValue: {
		type: Array,
		default: () => [],
	},
	defaultActiveGroups: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits(['update:modelValue']);

const openGroupMenus = ref({});
const selectedGroupColumns = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

const normalizedGroups = computed(() => {
	return (Array.isArray(props.groups) ? props.groups : [])
		.map((group, groupIndex) => {
			if (typeof group === 'string') {
				return {
					key: `${group}-${groupIndex}`,
					label: group,
					columnField: group,
					hasLevels: false,
					levels: [],
				};
			}

			const levels = (Array.isArray(group?.levels) ? group.levels : [])
				.map((level, levelIndex) => ({
					key: level?.key || `${group?.key || `group-${groupIndex}`}-level-${levelIndex + 1}`,
					label: level?.label || level?.name || `${level?.level || levelIndex + 1}`,
					columnField: level?.columnField || level?.field || null,
				}))
				.filter((level) => typeof level.columnField === 'string' && level.columnField.length > 0);

			return {
				key: group?.key || `group-${groupIndex}`,
				label: group?.label || group?.name || `Group ${groupIndex + 1}`,
				columnField: group?.columnField || group?.field || null,
				hasLevels: levels.length > 0,
				levels,
			};
		})
		.filter((group) => group.hasLevels || (typeof group.columnField === 'string' && group.columnField.length > 0));
});

watch(
	() => [props.defaultActiveGroups, props.groups],
	() => {
		applyDefaultActiveGroups();
	},
	{ deep: false, immediate: true }
);

function applyDefaultActiveGroups() {
	if (selectedGroupColumns.value.length > 0) {
		return;
	}

	const availableColumns = new Set(normalizedGroups.value.flatMap((group) => (group.hasLevels ? group.levels.map((level) => level.columnField) : [group.columnField])).filter(Boolean));
	const defaultColumns = [...new Set((Array.isArray(props.defaultActiveGroups) ? props.defaultActiveGroups : []).filter((columnField) => availableColumns.has(columnField)))];

	if (defaultColumns.length === 0) {
		return;
	}

	emitGroupColumns(defaultColumns);
}

function isSimpleGroupSelected(group) {
	return selectedGroupColumns.value.includes(group.columnField);
}

function isLevelGroupSelected(group) {
	return group.levels.some((level) => selectedGroupColumns.value.includes(level.columnField));
}

function getSelectedLevel(group) {
	return [...group.levels].reverse().find((level) => selectedGroupColumns.value.includes(level.columnField)) || null;
}

function getGroupDisplayLabel(group) {
	const selectedLevel = getSelectedLevel(group);
	return selectedLevel ? `${group.label} - ${selectedLevel.label}` : group.label;
}

const groupOrderByKey = computed(() => {
	const order = new Map();
	let nextRank = 1;
	selectedGroupColumns.value.forEach((columnField) => {
		const owningGroup = normalizedGroups.value.find((group) => (group.hasLevels ? group.levels.some((level) => level.columnField === columnField) : group.columnField === columnField));
		if (owningGroup && !order.has(owningGroup.key)) {
			order.set(owningGroup.key, nextRank++);
		}
	});
	return order;
});

function getGroupOrder(group) {
	return groupOrderByKey.value.get(group.key) || 0;
}

function emitGroupColumns(nextColumns) {
	emit('update:modelValue', [...new Set(nextColumns)]);
}

function toggleSimpleGroup(group) {
	if (!group?.columnField) {
		return;
	}

	const nextColumns = [...selectedGroupColumns.value];
	const existingIndex = nextColumns.indexOf(group.columnField);

	if (existingIndex >= 0) {
		nextColumns.splice(existingIndex, 1);
	} else {
		nextColumns.push(group.columnField);
	}

	emitGroupColumns(nextColumns);
}

function isLevelSelected(group, level) {
	if (!group || !level?.columnField) {
		return false;
	}

	const topLevel = getSelectedLevel(group);
	return topLevel?.columnField === level.columnField;
}

function selectGroupLevel(group, level) {
	if (!group || !level?.columnField) {
		return;
	}

	const currentTopLevel = getSelectedLevel(group);
	const isCurrentlySelected = currentTopLevel?.columnField === level.columnField;
	const filteredColumns = selectedGroupColumns.value.filter((columnField) => !group.levels.some((groupLevel) => groupLevel.columnField === columnField));

	if (!isCurrentlySelected) {
		const levelIndex = group.levels.findIndex((l) => l.columnField === level.columnField);
		const levelsToAdd = group.levels.slice(0, levelIndex + 1).map((l) => l.columnField);
		filteredColumns.push(...levelsToAdd);
	}

	emitGroupColumns(filteredColumns);
	setMenuOpen(group.key, false);
}

function isMenuOpen(groupKey) {
	return Boolean(openGroupMenus.value?.[groupKey]);
}

function setMenuOpen(groupKey, isOpen) {
	openGroupMenus.value[groupKey] = Boolean(isOpen);
}
</script>

<style scoped lang="scss">
.quick-row-group__order {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	margin-right: 6px;
	border-radius: 50%;
	background: rgb(var(--v-theme-primary));
	color: rgb(var(--v-theme-on-primary));
	font-size: 10px !important;
}
</style>
